import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs';
import { SkeletonModule } from 'primeng/skeleton';
import { DialogService } from 'primeng/dynamicdialog';
import { ActivatedRoute, Router } from '@angular/router';
import { AppButtonComponent } from '../../../../shared/core/ui';
import {
  ListPagination,
  ListPaginationDefaults,
  PaginationComponent,
  PlaceholderComponent,
  lastPageIfOutOfRange,
  listQueryParamsNavigation,
  readListPagination,
  toListPaginationQueryParams,
} from '../../../../shared';
import { ToastService } from '../../../../trash/shared/components';
import { handleApiErrors } from '../../../../common';
import { AddParkingTicketDialogComponent } from './add-parking-ticket-dialog/add-parking-ticket-dialog.component';
import { ParkingTicketCardComponent } from './parking-ticket-card/parking-ticket-card.component';
import { CustomerProfilesApiClientProvider, ICustomerProfilesApiClient } from '../../clients';
import { IReservationListResponse } from '../../dtos';
import { UserContextService } from '../../../../common/user-management/auth-management/authentication/services/user-context.service';

const PAGINATION: ListPaginationDefaults = { pageSize: 6, pageSizeOptions: [6, 12, 24] };

@Component({
  selector: 'app-parking-tickets',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    SkeletonModule,
    AppButtonComponent,
    PaginationComponent,
    PlaceholderComponent,
    ParkingTicketCardComponent,
  ],
  templateUrl: './parking-tickets.component.html',
  styleUrl: './parking-tickets.component.scss',
})
export class ParkingTicketsComponent implements OnInit {
  public static readonly SUCCESS_TICKET_STATUSES = new Set([
    'Settled',
    'Completed',
    'Fulfilled'
  ]);

  private client: ICustomerProfilesApiClient = inject(CustomerProfilesApiClientProvider).getClient();
  private toastService: ToastService = inject(ToastService);
  private dialog: DialogService = inject(DialogService);
  private router: Router = inject(Router);
  private route: ActivatedRoute = inject(ActivatedRoute);
  private destroyRef: DestroyRef = inject(DestroyRef);
  translate: TranslateService = inject(TranslateService);

  tickets: IReservationListResponse[] = [];
  isLoading = false;
  isAuthenticated = UserContextService.isAuthenticated();

  page = 1;
  pageSize = PAGINATION.pageSize;
  pageSizesPagination: number[] = PAGINATION.pageSizeOptions;
  totalRecords = 0;

  ngOnInit(): void {
    if (!this.isAuthenticated) {
      return;
    }
    // The page keeps its pagination in the URL, so Back from a ticket reopens the same page.
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const pagination = readListPagination(params, PAGINATION);
      this.page = pagination.page;
      this.pageSize = pagination.pageSize;
      this.getTickets();
    });
  }

  getTickets(): void {
    this.isLoading = true;
    this.client
      .getMyOffStreetReservations({ page: this.page, pageSize: this.pageSize })
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (response) => {
          // A page restored from the URL may no longer exist; load the last one rather than show an empty list.
          const lastPage = lastPageIfOutOfRange(
            { page: this.page, pageSize: this.pageSize },
            response.paginationMetadata.totalItemCount,
            response.items?.length ?? 0
          );
          if (lastPage !== null) {
            this.changePagination({ page: lastPage, pageSize: this.pageSize });
            return;
          }

          this.tickets = response.items;
          this.totalRecords = response.paginationMetadata.totalItemCount;
        },
        error: (error) => handleApiErrors(error, this.toastService),
      });
  }

  openAddTicketDialog(): void {
    const ref = this.dialog.open(AddParkingTicketDialogComponent, {
      header: this.translate.instant('ADD_TICKET.DIALOG_TITLE'),
      width: '560px',
      breakpoints: { '768px': '95vw' },
      styleClass: 'add-ticket-dialog',
    });
    ref?.onClose?.subscribe((ticketCode?: string) => {
      if (ticketCode) {
        this.toastService.showSuccess(
          this.translate.instant('GENERAL.SUCCESS'),
          this.translate.instant('ADD_TICKET.ADD_SUCCESS')
        );
        this.router.navigate(['/PKOFF', ticketCode]);
      }
    });
  }

  onPageChange(newPage: number): void {
    this.changePagination({ page: newPage, pageSize: this.pageSize });
  }

  onPageSizeChange(newSize: number): void {
    this.changePagination({ page: 1, pageSize: newSize });
  }

  /** The URL holds the pagination, so the fetch follows from the query-param change. */
  private changePagination(pagination: ListPagination): void {
    this.router.navigate([], listQueryParamsNavigation(this.route, toListPaginationQueryParams(pagination, PAGINATION)));
  }

  navigateToLogin(): void {
    this.router.navigate(['/auth/login']);
  }
}
